/**
 * AbsenceService - Handle all absence-related API operations
 */

import httpService, { HttpResponse } from '../../../core/services/HttpService';

// Absence types enum
export type AbsenceType = 'HOLIDAY' | 'OVERTIME' | 'SICK' | 'SICK_CHILD' | 'EXAM' | 'WISH_OFF';

// Absence status enum
export type AbsenceStatus = 'REQUESTED' | 'APPROVED' | 'REJECTED' | 'CANCELLED';

// Work area interface
export interface WorkArea {
  id: number;
  name: string;
  [key: string]: any;
}

// Absence interface
export interface Absence {
  id: number;
  employee: number;
  absence_type: AbsenceType;
  start_datetime: string;
  end_datetime: string;
  status: AbsenceStatus;
  reason_note?: string;
  requested_on?: string;
  full_day?: boolean;
  employee_work_areas?: number[];
  employee_name?: string;
  [key: string]: any;
}

// Absence form data interface (used in add/edit modals)
export interface AbsenceFormData {
  employee: string;
  absence_type: AbsenceType;
  start_date: string;
  full_day_start: boolean;
  start_day_start_time: string;
  start_day_end_time: string;
  end_date: string;
  full_day_end: boolean;
  end_day_start_time: string;
  end_day_end_time: string;
  reason_note: string;
  requested_on: string;
}

// Absence edit form data interface (includes id and status)
export interface AbsenceEditFormData extends AbsenceFormData {
  id: number | null;
  status: AbsenceStatus;
}

// Absence API payload interface
export interface AbsencePayload {
  employee: string;
  absence_type: AbsenceType;
  start_datetime: string;
  end_datetime: string;
  reason_note: string;
  requested_on: string | null;
  status: AbsenceStatus;
  full_day: boolean;
  admin: boolean;
}

class AbsenceService {
  /**
   * Fetch all absences
   */
  async fetchAllAbsences(): Promise<Absence[]> {
    const response: HttpResponse<Absence[]> = await httpService.get('/api/absences/');
    return response.data;
  }

  /**
   * Create a new absence
   */
  async createAbsence(payload: AbsencePayload): Promise<Absence> {
    const response: HttpResponse<Absence> = await httpService.post('/api/absences/', payload);
    return response.data;
  }

  /**
   * Update an existing absence
   */
  async updateAbsence(id: number, payload: AbsencePayload): Promise<Absence> {
    const response: HttpResponse<Absence> = await httpService.patch(`/api/absences/${id}/`, payload);
    return response.data;
  }

  /**
   * Delete an absence
   */
  async deleteAbsence(id: number): Promise<void> {
    await httpService.delete(`/api/absences/${id}/`);
  }

  /**
   * Approve an absence request
   */
  async approveAbsence(id: number): Promise<void> {
    await httpService.post(`/api/absences/${id}/approve/`);
  }

  /**
   * Reject an absence request
   */
  async rejectAbsence(id: number): Promise<void> {
    await httpService.post(`/api/absences/${id}/reject/`);
  }

  /**
   * Fetch all work areas
   */
  async fetchAllWorkAreas(): Promise<WorkArea[]> {
    const response: HttpResponse<WorkArea[]> = await httpService.get('/api/work-areas/');
    return response.data.filter(wa => (wa as any).is_active !== false);
  }
}

// Export singleton instance
const absenceService = new AbsenceService();
export default absenceService; 